/* eslint-disable camelcase */
/* eslint no-console: 0 */
import { useRouter } from 'next/router'
import posthog from 'posthog-js'
import { useCallback, useEffect, useMemo } from 'react'
import { useEffectOnce } from 'react-use'
import { MoisesCli } from '../../../lib/cli'
import ReactGA from '../../../lib/react-ga'
import { useRudderStack } from '../../../lib/rudderstack'
import {
  EventParams,
  EventTrackParams,
  UseEvent
} from '../../../types/events.types'
import { readShellDisabled } from '../../iframe/is-shell-disabled'
import { useShellGate } from '../../iframe/use-shell-gate'
import { useDesktopApp } from '../../misc/use-desktop-app'
import { useGetUtmStorage } from '../use-utm-storage/use-get-utm-storage'
import { formatUserAttributes } from './utils-format-user-attributes'
import { getDeviceInfo } from './utils-get-device-info'

interface Props {
  user: any
  MoisesCLI: MoisesCli
}

type MessageType = {
  data: {
    type: string
    payload?: any
  }
}

export const SESSION_STORAGE_KEY = 'moises:log_events'
export const ANALYTICS_HOST_MESSAGE_TYPE = 'moises:analytics'

export const useEvent = ({ user, MoisesCLI }: Props): UseEvent => {
  const { query } = useRouter()
  const rudderstack = useRudderStack()
  const { sendMessage } = useShellGate()
  const { getUTM } = useGetUtmStorage()
  const { isDesktopApp } = useDesktopApp()
  const { id: userId, shouldIdentifyOnCIO = false } = useMemo(
    () => user || {},
    [user]
  )

  useEffectOnce(() => {
    if (query?.log_events) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true')
    }
  })

  const currentClient = useMemo(() => {
    const isMobile = query?.mobile === 'true'
    const rawClient = query?.client

    if (isMobile && (rawClient === 'android' || rawClient === 'ios')) {
      return rawClient
    }

    return isDesktopApp ? 'studio_desktop_app' : 'studio'
  }, [query?.client, query?.mobile, isDesktopApp])

  const sendEvent = useCallback(
    ({
      name,
      category,
      label,
      customAttributes,
      fbEventId,
      userId: userIdByParams
    }: EventParams) => {
      if (!userId?.length && !userIdByParams?.length) {
        return
      }

      if (readShellDisabled()) {
        sendMessage({
          type: ANALYTICS_HOST_MESSAGE_TYPE,
          source: 'shell',
          payload: {
            channel: 'legacy',
            name,
            properties: {
              category,
              label,
              ...customAttributes,
              ...getUTM()
            },
            ...(fbEventId && { eventID: fbEventId })
          }
        })

        if (sessionStorage.getItem(SESSION_STORAGE_KEY)) {
          console.group('event (host):', name)
          console.table({
            category,
            label,
            ...customAttributes,
            ...getUTM()
          })
          console.groupEnd()
        }

        return
      }

      const userIdLogged = userIdByParams || userId

      const extendedCustomAttributes = {
        ...customAttributes,
        user_id: userIdLogged,
        locale: navigator.language,
        // @ts-expect-error
        device_os: window?.navigator?.userAgentData?.platform || 'not set',
        client: isDesktopApp ? 'studio-desktop-app' : 'studio',
        ...getUTM()
      }

      rudderstack.track(name, {
        category,
        label,
        ...{
          ...extendedCustomAttributes,
          shouldIdentifyOnCIO
        }
      })

      let extraFbParams
      if (fbEventId) extraFbParams = { eventID: fbEventId }

      window?.fbq?.(
        'track',
        name,
        {
          category,
          label: label || category,
          ...extendedCustomAttributes
        },
        extraFbParams
      )

      ReactGA.event({
        category,
        action: name,
        label: label || category,
        ...extendedCustomAttributes
      })

      if (sessionStorage.getItem(SESSION_STORAGE_KEY)) {
        console.group('event:', name)
        console.table({
          category,
          label,
          ...extendedCustomAttributes
        })
        console.groupEnd()
      }

      if (window.self !== window.top) {
        window.parent?.postMessage({ type: `${name}-confirmed` }, '*')
      }
    },
    [
      getUTM,
      shouldIdentifyOnCIO,
      userId,
      isDesktopApp,
      rudderstack,
      sendMessage
    ]
  )

  const sendEventPostHog = useCallback(
    ({
      name,
      event_category,
      customAttributes,
      transport,
      send_instantly,
      userId: userIdByParams
    }: EventParams) => {
      const deviceInfo = getDeviceInfo()

      const extendedCustomAttributes = {
        ...customAttributes,
        device_category: deviceInfo.device_category || 'not set',
        client: currentClient,
        ...(customAttributes?.source && {
          source: String(customAttributes?.source)?.split('?')[0]
        })
      }

      if (readShellDisabled()) {
        sendMessage({
          type: ANALYTICS_HOST_MESSAGE_TYPE,
          source: 'shell',
          payload: {
            channel: 'posthog',
            name,
            properties: {
              event_category,
              ...customAttributes,
              ...(customAttributes?.source && {
                source: String(customAttributes?.source)?.split('?')[0]
              })
            },
            ...(transport && { transport }),
            ...(send_instantly !== undefined && { send_instantly })
          }
        })

        if (sessionStorage.getItem(SESSION_STORAGE_KEY)) {
          console.group('posthog event (host):', name)
          console.table({
            event_category,
            ...extendedCustomAttributes
          })
          console.groupEnd()
        }

        return
      }

      const personProperties = user
        ? formatUserAttributes(user, query?.source as string)
        : {}

      posthog.capture(
        name,
        {
          event_category,
          ...extendedCustomAttributes,
          user_id: userIdByParams || userId,
          $set: personProperties
        },
        {
          ...(transport && { transport }),
          ...(send_instantly && { send_instantly })
        }
      )

      if (sessionStorage.getItem(SESSION_STORAGE_KEY)) {
        console.group('posthog event:', name)
        console.table({
          event_category,
          ...extendedCustomAttributes,
          user_id: userIdByParams || userId
        })
        console.groupEnd()
      }
    },
    [userId, currentClient, user, query?.source, sendMessage]
  )

  const eventTrack = useCallback(
    async ({ data }: EventTrackParams) => {
      const extendedCustomAttributes = {
        ...data.payload,
        user_id: user?.id,
        locale: navigator.language,
        // @ts-expect-error
        device_os: window?.navigator?.userAgentData?.platform || 'not set',
        client: currentClient,
        timestamp: new Date().toISOString()
      }

      const event = {
        event: data.event,
        payload: extendedCustomAttributes
      }

      await MoisesCLI.eventTrack({
        data: event
      })
    },
    [MoisesCLI, user?.id, currentClient]
  )

  useEffect(() => {
    if (!shouldIdentifyOnCIO) {
      const handleMessage = (event: MessageType): void => {
        const message = event.data

        if (['events-voice-studio'].includes(message.type)) {
          sendEvent(message.payload)
        }
      }

      window.addEventListener('message', handleMessage)
      return () => {
        window.removeEventListener('message', handleMessage)
      }
    }
    return () => {}
    // eslint-disable-next-line
  }, [sendEvent])

  return {
    sendEvent,
    eventTrack,
    sendEventPostHog,
    rudderstack
  }
}
